import { Button, Input, Popconfirm, Space, Table, message } from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import { useNavigate } from "react-router-dom";
import { EntityForm } from "../../components/EntityForm";
import { useEffect, useMemo, useState } from "react";
import { createUser, deleteUser, getUsers, updateUser } from "../../api/user";

import { DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import { NoPermission } from "../../components/EntityForm/NoPermision/noPermission";
import { getFileUrl } from "../../api/file";
import { RateBy } from "../../helpers/rate";

export const UserRoles = {
  Admin: "admin",
  User: "web_user",
  Employee: "employee",
};

export function User({ user }) {
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(5);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState(null);
  const dataIndex = (page - 1) * perPage;

  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [showCreateUser, setShowCreateUser] = useState(false);
  const [editUser, setEditUser] = useState(null);

  const sections = useMemo(() => {
    const attachments = [];
    const avatars = [];
    if (editUser) {
      attachments.push(
        ...(editUser.attachments || []).map((attachment) => {
          return {
            ...attachment,
            uid: attachment.id,
            name: attachment.originalName,
            status: "done",
            url: getFileUrl(attachment),
          };
        })
      );
      if (editUser.avatar)
        avatars.push({
          ...editUser.avatar,
          uid: editUser.avatar.id,
          name: editUser.avatar.originalName,
          status: "done",
          url: getFileUrl(editUser.avatar),
        });
    }
    return [
      {
        items: [
          {
            type: "avatar",
            label: "User Profile Picture (2MB)",
            name: "avatar",
            col: 12,
            maxLen: 200,
            fileList: avatars,
            fields: { entity: "user" },
          },
        ],
      },
      {
        items: [
          {
            type: "text",
            label: "First Name",
            name: "firstName",
            required: true,
          },
          {
            type: "text",
            label: "Last Name",
            name: "lastName",
            required: false,
          },
          {
            type: "async-select",
            label: "Role",
            name: "role",
            required: true,
            options: Object.keys(UserRoles).map((role) => {
              return {
                value: UserRoles[role],
                label: role,
              };
            }),
          },
        ],
      },
      {
        items: [
          {
            type: "email",
            label: "Email",
            name: "email",
            required: true,
          },
          {
            type: "text",
            label: "Phone",
            name: "phone",
            required: false,
          },
        ],
      },
      {
        items: [
          {
            type: "number",
            label: "Rate",
            name: "rate",
            addonBefore: "AUD",
            addonAfter: "$",
            required: false,
            col: 5,
          },
          {
            type: "async-select",
            label: "Rate By",
            name: "rateBy",
            required: false,
            options: Object.keys(RateBy).map((rateBy) => {
              return {
                value: RateBy[rateBy],
                label: rateBy,
              };
            }),
            col: 3,
          },
        ],
      },
      {
        items: [
          {
            type: "password",
            label: "Password",
            name: "password",
            required: !!!editUser,
          },
          {
            type: "password",
            label: "Confirm Password",
            name: "confirmPassword",
            required: !!!editUser,
          },
        ],
      },
      {
        items: [
          {
            type: "attachments",
            label: "Attachments (2MB)",
            additionalInfo:
              "Work permit, insurance, , license, green card etc.",
            name: "attachments",
            col: 12,
            maxLen: 200,
            fileList: attachments,
            fields: { entity: "user" },
          },
        ],
      },
    ];
  }, [editUser]);

  const fetchUsers = async ({ page, q }) => {
    try {
      setIsLoading(true);
      const { total, results } = await getUsers({ page, perPage, q });
      setTotal(total);
      setUsers(results);
      setIsLoading(false);
    } catch (err) {
      console.error(err);
      message.error("Something went wrong");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!user || user.role !== UserRoles.Admin) return;
    fetchUsers({ page, perPage, q: searchQuery });
  }, [user, page, searchQuery]);

  const handleSubmit = async ({
    password,
    confirmPassword,
    rate,
    rateBy,
    ...userData
  }) => {
    if (password !== confirmPassword) {
      message.error("Password and confirm password must be the same");
      throw new Error("Password and confirm password must be the same");
    }

    const avatar =
      userData.avatar &&
      userData.avatar.fileList &&
      userData.avatar.fileList.length
        ? userData.avatar.fileList.map((file) => {
            if (!file.response) return file;
            return file.response.file;
          })[0]
        : userData.avatar;

    const attachments =
      userData.attachments && userData.attachments.fileList
        ? userData.attachments.fileList.map((file) => {
            if (!file.response) return file;
            return file.response.file;
          })
        : userData.attachments;

    const rateData = rate ? { rate, rateBy } : undefined;

    if (editUser) {
      await updateUser({
        ...userData,
        password,
        attachments,
        avatar,
        id: editUser.id,
        rate: rateData,
      });
    } else {
      await createUser({
        ...userData,
        attachments,
        avatar,
        password,
        rate: rateData,
      });
    }
    setEditUser(null);
    setShowCreateUser(false);
    fetchUsers({ page, perPage });
  };

  const handleDelete = async (user) => {
    await deleteUser(user.id);
    fetchUsers(page, perPage);
  };

  const TableCols = [
    { title: "No", dataIndex: "no", key: "no", width: 80 },
    { title: "First Name", dataIndex: "firstName", key: "name" },
    { title: "Last Name", dataIndex: "lastName", key: "leadName" },
    { title: "Role", dataIndex: "role", key: "role" },
    {
      title: "Action",
      key: "action",
      render: (text, record, index) => (
        <Space size={2}>
          <Popconfirm
            title="Delete the task"
            description="Are you sure to delete this user?"
            onConfirm={() => handleDelete(users[index])}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" icon={<DeleteOutlined />} danger></Button>
          </Popconfirm>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => setEditUser(users[index])}
          ></Button>
        </Space>
      ),
    },
  ];

  if (user && user.role !== UserRoles.Admin)
    return <NoPermission role={user.role} />;

  return (
    <Space direction="vertical" style={{ width: "100%" }} className="p-6">
      <Space style={{ width: "100%", justifyContent: "space-between" }}>
        <PageHeader
          onBack={() => navigate(-1)}
          title="Users"
          subTitle="List of users in company"
        />

        <Button type="primary" onClick={() => setShowCreateUser(true)}>
          Create User
        </Button>
      </Space>

      <Space>
        <Input.Search
          className="search"
          placeholder="Name"
          onSearch={(str) => {
            setSearchQuery(str);
            setPage(1);
          }}
        />
      </Space>

      <Table
        dataSource={users.map((user, index) => {
          return {
            no: dataIndex + index + 1,
            ...user,
            role: Object.keys(UserRoles).find(
              (roleName) => UserRoles[roleName] === user.role
            ),
          };
        })}
        columns={TableCols}
        loading={isLoading}
        pagination={{
          size: "small",
          total,
          pageSize: 5,
          current: parseInt(page),
          onChange: setPage,
          showQuickJumper: true,
          showTotal: () => `Total ${total} users`,
          position: ["bottomLeft"],
        }}
      />

      {(showCreateUser || editUser) && (
        <EntityForm
          initialValues={{
            ...editUser,
            rate:
              editUser && editUser.rate && parseFloat(editUser.rate.rate)
                ? editUser.rate.rate
                : undefined,
            rateBy:
              editUser && editUser.rate ? editUser.rate.rateBy : undefined,
          }}
          sections={sections}
          title="User"
          open={showCreateUser || editUser}
          onCancel={() => {
            setShowCreateUser(false);
            setEditUser(null);
          }}
          onSubmit={handleSubmit}
          onDelete={() => handleDelete(editUser).then(() => setEditUser(null))}
          isEditMode={!!editUser}
        ></EntityForm>
      )}
    </Space>
  );
}
